import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * WidgetPreview — Live preview of the REAL widget (single source of truth).
 *
 * Mounts the actual `pingup-chat-widget-v2` web component inside an iframe in
 * "preview mode" (no backend calls). To render the desktop layout correctly we
 * give the iframe a virtual DESKTOP width (1024px, above the widget's 768px
 * mobile breakpoint) and scale it down with CSS transform to fit the preview
 * box — the same trick browser device-emulators use. For mobile we use a real
 * phone width so the widget's responsive/fullscreen layout kicks in.
 *
 * Config is pushed live via postMessage → the widget's updateConfig().
 */
function buildSrcDoc(origin, cacheBust) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      html, body { margin: 0; height: 100%; background: transparent; overflow: hidden; }
    </style>
  </head>
  <body>
    <script src="${origin}/widget.build.js?t=${cacheBust}"><\/script>
    <script>
      (function () {
        var el = null;
        var got = false;
        var lastMode = null;
        function ready() {
          return !!(window.customElements && window.customElements.get('pingup-chat-widget-v2'));
        }
        function openSoon() {
          // Insiste finche' la chat non risulta davvero visibile: con init
          // lenti (primo load dell'iframe) 8 tentativi non bastavano e la
          // preview restava sulla bolla chiusa.
          var tries = 0;
          var iv = setInterval(function () {
            tries++;
            try {
              if (el && el.open) el.open();
              var sr = el && el.shadowRoot;
              var chat = sr && sr.querySelector('.puw2-chat');
              if (chat && getComputedStyle(chat).display !== 'none') {
                clearInterval(iv);
                return;
              }
            } catch (e) {}
            if (tries > 40) clearInterval(iv);
          }, 150);
        }
        function pillEnabled(cfg) {
          var b = (cfg && cfg.behavior) || {};
          return !!(b.externalCTA && b.externalCTA.enabled);
        }
        function tune(cfg) {
          // Preview tweaks: show the pill quickly and keep it visible.
          var preview = Object.assign({}, cfg, { preview: true });
          if (pillEnabled(cfg)) {
            preview.behavior = Object.assign({}, preview.behavior);
            preview.behavior.externalCTA = Object.assign(
              {}, preview.behavior.externalCTA, { showDelay: 600, autoHide: false }
            );
          }
          return preview;
        }
        function apply(cfg) {
          if (!ready()) { setTimeout(function () { apply(cfg); }, 60); return; }
          var mode = pillEnabled(cfg); // true = show pill (chat stays closed)
          var preview = tune(cfg);
          // When the pill is toggled, recreate the widget so it starts in the
          // right state (open chat vs closed-with-pill).
          if (el && lastMode !== null && lastMode !== mode) {
            try { el.remove(); } catch (e) {}
            el = null;
          }
          lastMode = mode;
          if (!el) {
            el = document.createElement('pingup-chat-widget-v2');
            document.body.appendChild(el);
            Promise.resolve(el.init(preview)).then(function () { if (!mode) openSoon(); });
          } else {
            el.updateConfig(preview);
            if (!mode) openSoon();
          }
        }
        window.addEventListener('message', function (e) {
          if (e.data && e.data.type === 'pingup-preview-config') {
            got = true;
            apply(e.data.config);
          }
        });
        function ask() { try { window.parent.postMessage({ type: 'pingup-preview-ready' }, '*'); } catch (e) {} }
        ask();
        var asker = setInterval(function () { if (got) clearInterval(asker); else ask(); }, 250);
      })();
    <\/script>
  </body>
</html>`;
}

const DESKTOP_VW = 1024; // above the 768px mobile breakpoint
const DESKTOP_VH = 900; // bigger virtual height → narrower chat (same height)
const MOBILE_VW = 390;
const MOBILE_VH = 740;

// Map the backend product shape (from /products/preview) to the widget's card
// shape (what Messages.jsx renders inside a `rag-mini-card`).
function productToCard(p) {
  if (!p) return null;
  const sp = p.schema_product || {};
  return {
    title: p.title,
    image: p.image_url,
    url: p.canonical_url,
    brand: sp.brand,
    price: sp.price,
    currency: sp.currency,
  };
}

const WidgetPreview = ({
  config,
  device = "desktop",
  product = null,
  demo = null,
  // Altezza del viewport desktop virtuale: più bassa → chat più corta e
  // scala maggiore nel box (560 ≈ scala 1:1 in un box alto 560px).
  desktopVH = DESKTOP_VH,
}) => {
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const configRef = useRef(config);
  configRef.current = config;
  const deviceRef = useRef(device);
  deviceRef.current = device;
  const productRef = useRef(product);
  productRef.current = product;
  const demoRef = useRef(demo);
  demoRef.current = demo;

  const [dims, setDims] = useState({ w: 0, h: 0 });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cacheBust = useRef(Date.now()).current;
  const srcDoc = useMemo(
    () => buildSrcDoc(origin, cacheBust),
    [origin, cacheBust]
  );

  const pushConfig = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: "pingup-preview-config",
        config: {
          ...(configRef.current || {}),
          previewDevice: deviceRef.current,
        },
      },
      "*"
    );
  };

  // Send the loaded product (or null to clear) to the widget so it shows it as
  // a card in the live chat preview.
  const pushProduct = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: "pingup-preview-product",
        card: productToCard(productRef.current),
      },
      "*"
    );
  };

  // Send the demo element flags so the widget shows each chat element live.
  const pushDemo = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: "pingup-preview-demo",
        elements: demoRef.current || {},
      },
      "*"
    );
  };

  // Measure the available box so we can scale the virtual viewport to fit.
  useLayoutEffect(() => {
    const measure = () => {
      if (wrapRef.current) {
        const r = wrapRef.current.getBoundingClientRect();
        setDims({ w: Math.round(r.width), h: Math.round(r.height) });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Respond to the iframe's "ready" pings with the current config.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === "pingup-preview-ready") {
        pushConfig();
        pushProduct();
        pushDemo();
      }
      // The widget asks for the current product when it (re)mounts in preview.
      if (e.data?.type === "pingup-preview-product-request") pushProduct();
      // ...and for the current demo flags on (re)mount.
      if (e.data?.type === "pingup-preview-demo-request") pushDemo();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-send config whenever it (or the device) changes (live updates).
  useEffect(() => {
    pushConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, device]);

  // Re-send the product whenever it changes (load/clear in the Anteprima tab).
  useEffect(() => {
    pushProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  // Re-send the demo flags whenever a toggle changes in the Anteprima tab.
  useEffect(() => {
    pushDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo]);

  // Fixed virtual viewport (fixed aspect ratio) so the widget keeps its
  // proportions. Scale by HEIGHT so the chat is always as tall as the real
  // widget (~75% of the panel height); the width follows proportionally, so
  // resizing the panel width does NOT squash it. Anchored bottom-right where
  // the corner widget lives; any extra horizontal space is transparent.
  const virtualW = device === "mobile" ? MOBILE_VW : DESKTOP_VW;
  const virtualH = device === "mobile" ? MOBILE_VH : desktopVH;
  let scale, left, top;
  if (device === "mobile") {
    // Mobile renders fullscreen, so the whole phone viewport must fit inside the
    // box. Scale by the TIGHTER of width/height and center it — otherwise the
    // height-scaled phone is wider than the narrow box and gets clipped left.
    scale = Math.min(
      dims.w > 0 ? dims.w / virtualW : 1,
      dims.h > 0 ? dims.h / virtualH : 1
    );
    left = (dims.w - virtualW * scale) / 2; // centered horizontally
    top = (dims.h - virtualH * scale) / 2; // centered vertically
  } else {
    // Desktop: scale by HEIGHT. The corner widget lives bottom-right, so anchor
    // there; the searchbar is bottom-CENTER, so center it instead.
    const isSearchbar = config?.mode === "searchbar";
    const byHeight = dims.h > 0 ? dims.h / virtualH : 1;
    // La barra sta al centro: in un riquadro stretto, scalata per altezza, usciva
    // dai lati. Deve starci tutta in larghezza (barra + margini).
    const barWidth = Math.min(virtualW, (config?.searchbar?.maxWidth || 672) + 64);
    scale = isSearchbar && dims.w > 0 ? Math.min(byHeight, dims.w / barWidth) : byHeight;
    left = isSearchbar
      ? (dims.w - virtualW * scale) / 2
      : dims.w - virtualW * scale;
    // Appoggiata in fondo al riquadro. Prima un "-40" la alzava: restava uno
    // spazio vuoto sotto e la cima della chat veniva tagliata.
    top = dims.h - virtualH * scale;
  }

  return (
    <div
      ref={wrapRef}
      style={{ position: "absolute", inset: 0, overflow: "hidden" }}
    >
      <iframe
        ref={iframeRef}
        title="Widget preview"
        srcDoc={srcDoc}
        style={{
          position: "absolute",
          top,
          left,
          width: virtualW,
          height: virtualH,
          border: "none",
          background: "transparent",
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
        }}
      />
    </div>
  );
};

export default WidgetPreview;
